// Simple placeholder image utilities
// These replace external placeholder services that may be unreliable

/**
 * Generate a simple SVG placeholder as a data URL
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {string} type - Type of placeholder ('user', 'dentist', or 'text')
 * @param {string} text - Text to display (only used if type is 'text')
 * @param {string} bgColor - Background color (hex)
 * @param {string} iconColor - Icon/text color (hex)
 * @returns {string} Data URL for the placeholder
 */
export const createPlaceholder = (width = 50, height = 50, type = "user", text = "No Photo", bgColor = "#f9f9fa", iconColor = "#6c757d") => {
  let content = '';
  
  if (type === 'user') {
    // User profile icon (head and shoulders)
    const headRadius = Math.min(width, height) * 0.25;
    const headCenterX = width / 2;
    const headCenterY = height * 0.35;
    const bodyWidth = Math.min(width, height) * 0.6;
    const bodyHeight = Math.min(width, height) * 0.4;
    const bodyY = height * 0.55;
    
    content = `
      <circle cx="${headCenterX}" cy="${headCenterY}" r="${headRadius}" fill="${iconColor}"/>
      <path d="M${headCenterX - bodyWidth/2} ${bodyY} 
               Q${headCenterX - bodyWidth/2} ${bodyY + bodyHeight} ${headCenterX} ${bodyY + bodyHeight}
               Q${headCenterX + bodyWidth/2} ${bodyY + bodyHeight} ${headCenterX + bodyWidth/2} ${bodyY}
               Z" fill="${iconColor}"/>
    `;
  } else if (type === 'dentist') {
    // Dentist icon (stethoscope or medical cross)
    const centerX = width / 2;
    const centerY = height / 2;
    const size = Math.min(width, height) * 0.3;
    
    content = `
      <path d="M${centerX - size/2} ${centerY - size/2} 
               L${centerX + size/2} ${centerY - size/2}
               L${centerX + size/2} ${centerY + size/2}
               L${centerX - size/2} ${centerY + size/2}
               Z" fill="${iconColor}"/>
      <path d="M${centerX - size/4} ${centerY - size/4} 
               L${centerX + size/4} ${centerY - size/4}
               L${centerX + size/4} ${centerY + size/4}
               L${centerX - size/4} ${centerY + size/4}
               Z" fill="${bgColor}"/>
      <path d="M${centerX} ${centerY - size/4} 
               L${centerX} ${centerY + size/4}
               M${centerX - size/4} ${centerY}
               L${centerX + size/4} ${centerY}" stroke="${iconColor}" stroke-width="2"/>
    `;
  } else {
    // Text fallback
    content = `
      <text x="${width/2}" y="${height/2}" font-family="Arial, sans-serif" font-size="${Math.min(width, height) * 0.2}" fill="${iconColor}" text-anchor="middle" dy=".3em">${text}</text>
    `;
  }
  
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" fill="${bgColor}"/>
      <rect x="0" y="0" width="${width}" height="${height}" stroke="#ddd" stroke-width="1"/>
      ${content}
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Pre-defined placeholders for common use cases
export const PLACEHOLDERS = {
  PROFILE_50: createPlaceholder(50, 50, "user", "", "#f9f9fa", "#6c757d"),
  PROFILE_60: createPlaceholder(60, 60, "user", "", "#f9f9fa", "#6c757d"),
  PROFILE_120: createPlaceholder(120, 120, "user", "", "#f8f9fa", "#6c757d"),
  DENTIST_80: createPlaceholder(80, 80, "dentist", "", "#e5f5fc", "#1976d2"),
  DENTIST_60: createPlaceholder(60, 60, "dentist", "", "#e5f5fc", "#1976d2"),
  PATIENT_120: createPlaceholder(120, 120, "user", "", "#f8f9fa", "#6c757d"),
};
